'use client';

import Link from 'next/link';

export default function PrivacyPolicyPage() {
    return (
        <div className="min-h-screen bg-[#FAFAFA]">
            {/* Header */}
            <header className="bg-white border-b border-gray-200">
                <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
                    <Link href="/" className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-violet-600 flex items-center justify-center">
                            <span className="text-white font-bold text-sm">W</span>
                        </div>
                        <span className="text-lg font-semibold text-gray-900">Waitlistly</span>
                    </Link>
                    <Link
                        href="/"
                        className="text-sm text-gray-500 hover:text-gray-900 transition-colors"
                    >
                        ← Back to Home
                    </Link>
                </div>
            </header>

            {/* Content */}
            <main className="max-w-4xl mx-auto px-6 py-16">
                <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
                <p className="text-gray-500 mb-12">Last updated: January 2026</p>

                <div className="prose prose-gray max-w-none">
                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
                        <p className="text-gray-600 leading-relaxed mb-4">
                            Waitlistly ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our landing page builder service.
                        </p>
                        <p className="text-gray-600 leading-relaxed">
                            By using Waitlistly, you agree to the collection and use of information in accordance with this policy.
                        </p>
                    </section>

                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>

                        <h3 className="text-lg font-medium text-gray-800 mb-3">2.1 Account Information</h3>
                        <p className="text-gray-600 leading-relaxed mb-4">
                            When you create an account, we collect your email address and any profile information you choose to provide. This information is necessary to create and manage your account.
                        </p>

                        <h3 className="text-lg font-medium text-gray-800 mb-3">2.2 Landing Page Content</h3>
                        <p className="text-gray-600 leading-relaxed mb-4">
                            We store the content you create using our AI wizard, including product descriptions, copy, and page configurations. This data is stored securely and used only to provide our service.
                        </p>

                        <h3 className="text-lg font-medium text-gray-800 mb-3">2.3 Collected Emails (CRM Data)</h3>
                        <p className="text-gray-600 leading-relaxed mb-4">
                            When visitors sign up on your published landing pages, we collect and store their email addresses and any additional form fields you've configured. You own this data and can export or delete it at any time.
                        </p>

                        <h3 className="text-lg font-medium text-gray-800 mb-3">2.4 Usage Analytics</h3>
                        <p className="text-gray-600 leading-relaxed">
                            We collect anonymous usage data including page views, conversion rates, and general analytics to help you understand your landing page performance.
                        </p>
                    </section>

                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
                        <ul className="list-disc pl-6 text-gray-600 space-y-2">
                            <li>To provide, operate, and maintain our service</li>
                            <li>To generate AI-powered landing page content based on your inputs</li>
                            <li>To process payments and publish your landing pages</li>
                            <li>To send you service-related notifications and updates</li>
                            <li>To improve and personalize your experience</li>
                            <li>To detect and prevent fraud or abuse</li>
                        </ul>
                    </section>

                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Data Security</h2>
                        <p className="text-gray-600 leading-relaxed mb-4">
                            We implement industry-standard security measures to protect your data, including:
                        </p>
                        <ul className="list-disc pl-6 text-gray-600 space-y-2">
                            <li>SSL/TLS encryption for all data in transit</li>
                            <li>Secure database storage with encryption at rest</li>
                            <li>Regular security audits and monitoring</li>
                            <li>Access controls and authentication mechanisms</li>
                        </ul>
                    </section>

                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Third-Party Services</h2>
                        <p className="text-gray-600 leading-relaxed mb-4">
                            We use the following third-party services to operate Waitlistly:
                        </p>
                        <ul className="list-disc pl-6 text-gray-600 space-y-2">
                            <li><strong>Supabase:</strong> Database and authentication services</li>
                            <li><strong>AI Providers:</strong> To generate landing page content (OpenAI, Google Gemini)</li>
                            <li><strong>Payment Processors:</strong> To handle payment transactions securely</li>
                        </ul>
                        <p className="text-gray-600 leading-relaxed mt-4">
                            These services have their own privacy policies governing their use of your data.
                        </p>
                    </section>

                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Your Rights</h2>
                        <p className="text-gray-600 leading-relaxed mb-4">You have the right to:</p>
                        <ul className="list-disc pl-6 text-gray-600 space-y-2">
                            <li><strong>Access:</strong> Request a copy of your personal data</li>
                            <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                            <li><strong>Deletion:</strong> Request deletion of your account and data</li>
                            <li><strong>Export:</strong> Download your collected email leads in CSV format</li>
                            <li><strong>Withdraw Consent:</strong> Opt out of marketing communications</li>
                        </ul>
                    </section>

                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Data Retention</h2>
                        <p className="text-gray-600 leading-relaxed">
                            We retain your data for as long as your account is active or as needed to provide our services. If you delete your account, we will delete your personal data within 30 days, except where required by law.
                        </p>
                    </section>

                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Contact Us</h2>
                        <p className="text-gray-600 leading-relaxed">
                            If you have any questions about this Privacy Policy or our data practices, please contact us at:
                        </p>
                        <p className="text-gray-600 mt-4">
                            <strong>Email:</strong> hello@waitlistly.live<br />
                        </p>
                    </section>
                </div>
            </main>

            {/* Footer */}
            <footer className="border-t border-gray-200 py-8">
                <div className="max-w-4xl mx-auto px-6 flex justify-center items-center gap-4">
                    <div className="flex items-center gap-6 text-sm text-gray-500">
                        <Link href="/terms" className="hover:text-gray-900 transition-colors">Terms of Service</Link>
                        <Link href="/privacy" className="text-violet-600 font-medium">Privacy Policy</Link>
                        <Link href="/contact" className="hover:text-gray-900 transition-colors">Contact</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
}
